import { useCallback, useEffect, useState } from "react";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";
import {
  gameService,
  GameStateRow,
  RoundRow,
  QuestionRow,
  RoundLifelineRow,
  BonusRoundRow,
  ClassicBonusAnswerRow,
  ClassicBonusTeamResponseRow,
  ClosestGuessQuestionRow,
  QuestionAnswerRow,
} from "../../data/gameService";
import { useSupabase } from "../../auth/useSupabase";
import { GameStateCurrentPhase, Tables } from "../../../database.types";
import { App } from "../../types/types";
import { useParams } from "react-router-dom";
import StandardRound from "./StandardRound";
import ClassicBonusRoundAdmin from "./ClassicBonusRoundAdmin";
import { teamService } from "../../data/teamService";
import ClosestGuessBonusRoundAdmin from "./ClosestGuessBonusRoundAdmin";
import { StandardButton } from "../../components/StandardButton";

const ControlCenter = () => {
  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------
  const { gameId } = useParams<{ gameId: string }>();
  const { supabase } = useSupabase();
  const theme = useTheme();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState<Tables<"games"> | null>(null);
  const [team1, setTeam1] = useState<App.TeamWithMembers | null>(null);
  const [team2, setTeam2] = useState<App.TeamWithMembers | null>(null);
  const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswerRow[]>(
    [],
  );
  const [gameState, setGameState] = useState<GameStateRow | null>(null);
  const [rounds, setRounds] = useState<RoundRow[]>([]);
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [lifelines, setLifelines] = useState<RoundLifelineRow[]>([]);
  const [bonusRounds, setBonusRounds] = useState<BonusRoundRow[]>([]);
  const [classicBonusAnswers, setClassicBonusAnswers] = useState<
    ClassicBonusAnswerRow[]
  >([]);
  const [classicBonusTeamResponses, setClassicBonusTeamResponses] = useState<
    ClassicBonusTeamResponseRow[]
  >([]);
  const [closestGuessQuestions, setClosestGuessQuestions] = useState<
    ClosestGuessQuestionRow[]
  >([]);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------
  const team1Id = game?.team_one_id ?? "";
  const team2Id = game?.team_two_id ?? "";
  const team1Name = team1?.name ?? "Team 1";
  const team2Name = team2?.name ?? "Team 2";

  // ---------------------------------------------------------------------------
  // Callbacks
  // ---------------------------------------------------------------------------
  const fetchData = useCallback(async () => {
    try {
      if (!gameId) return;
      setLoading(true);

      const fullGame = await gameService.getFullGame(supabase, gameId);
      if (!fullGame) throw new Error("Game not found");

      const [team1Res, team2Res] = await Promise.all([
        fullGame.game.team_one_id
          ? teamService.getByIdWithMembers(supabase, fullGame.game.team_one_id)
          : null,
        fullGame.game.team_two_id
          ? teamService.getByIdWithMembers(supabase, fullGame.game.team_two_id)
          : null,
      ]);

      setGame(fullGame.game);
      setTeam1(team1Res);
      setTeam2(team2Res);
      setQuestionAnswers(fullGame.questionAnswers);
      setGameState(fullGame.gameState);
      setRounds(fullGame.rounds);
      setQuestions(fullGame.questions);
      setLifelines(fullGame.lifelines);
      setBonusRounds(fullGame.bonusRounds);
      setClassicBonusAnswers(fullGame.classicBonusAnswers);
      setClassicBonusTeamResponses(fullGame.classicBonusTeamResponses);
      setClosestGuessQuestions(fullGame.closestGuessQuestions);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setIsInitialRender(false);
    }
  }, [gameId, supabase]);

  const handleStartGame = useCallback(async () => {
    if (!game) return;
    await gameService.upsertGameState(supabase, {
      game_id: game.id,
      current_phase: "standard",
      current_round_number: 1,
      current_question_num: 1,
      show_question: false,
      show_answer: false,
      is_steal: false,
    });
    await fetchData();
  }, [supabase, game, fetchData]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (loading && isInitialRender) {
    return (
      <Box
        height="100vh"
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor={theme.palette.primary.dark}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!game) {
    return (
      <Box>
        <Typography variant="h3">
          Game not found, please refresh the page
        </Typography>
      </Box>
    );
  }

  if (!game.team_one_id || !game.team_two_id) {
    return (
      <Box>
        <Typography>Missing team {!game.team_one_id ? 1 : 2} id.</Typography>
      </Box>
    );
  }

  // Standard round
  if (
    gameState &&
    gameState.current_phase === "standard" &&
    Number.isFinite(gameState.current_round_number) &&
    team1 &&
    team2
  ) {
    return (
      <StandardRound
        game={game}
        gameState={gameState}
        rounds={rounds}
        questions={questions}
        lifelines={lifelines}
        bonusRounds={bonusRounds}
        classicBonusAnswers={classicBonusAnswers}
        classicBonusTeamResponses={classicBonusTeamResponses}
        closestGuessQuestions={closestGuessQuestions}
        team1={team1}
        team2={team2}
        gameId={game.id}
        questionAnswers={questionAnswers}
        refetchData={fetchData}
      />
    );
  }

  // Bonus round
  if (
    gameState?.current_phase &&
    (gameState.current_phase as GameStateCurrentPhase) === "bonus"
  ) {
    const bonusBr = bonusRounds.find((br) => br.phase === "bonus");

    if (bonusBr?.type === "Classic (List)" && gameState) {
      return (
        <ClassicBonusRoundAdmin
          bonusRound={bonusBr}
          classicBonusAnswers={classicBonusAnswers.filter(
            (a) => a.bonus_round_id === bonusBr.id,
          )}
          classicBonusTeamResponses={classicBonusTeamResponses.filter(
            (r) => r.bonus_round_id === bonusBr.id,
          )}
          team1Id={team1Id}
          team2Id={team2Id}
          team1Name={team1Name}
          team2Name={team2Name}
          gameId={game.id}
          gameState={gameState}
          refetchData={fetchData}
        />
      );
    }
    if (bonusBr?.type === "Closest Guess Battle" && gameState) {
      return (
        <ClosestGuessBonusRoundAdmin
          bonusRound={bonusBr}
          closestGuessQuestions={closestGuessQuestions.filter(
            (q) => q.bonus_round_id === bonusBr.id,
          )}
          team1Name={team1Name}
          team2Name={team2Name}
          gameId={game.id}
          gameState={gameState}
          refetchData={fetchData}
        />
      );
    }
  }

  // Overtime
  if (
    gameState?.current_phase &&
    (gameState.current_phase as GameStateCurrentPhase) === "overtime"
  ) {
    const overtimeBr = bonusRounds.find((br) => br.phase === "overtime");

    if (overtimeBr?.type === "Classic (List)" && gameState) {
      return (
        <ClassicBonusRoundAdmin
          bonusRound={overtimeBr}
          classicBonusAnswers={classicBonusAnswers.filter(
            (a) => a.bonus_round_id === overtimeBr.id,
          )}
          classicBonusTeamResponses={classicBonusTeamResponses.filter(
            (r) => r.bonus_round_id === overtimeBr.id,
          )}
          team1Id={team1Id}
          team2Id={team2Id}
          team1Name={team1Name}
          team2Name={team2Name}
          gameId={game.id}
          gameState={gameState}
          refetchData={fetchData}
        />
      );
    }

    if (overtimeBr?.type === "Closest Guess Battle" && gameState) {
      return (
        <ClosestGuessBonusRoundAdmin
          bonusRound={overtimeBr}
          closestGuessQuestions={closestGuessQuestions.filter(
            (q) => q.bonus_round_id === overtimeBr.id,
          )}
          team1Name={team1Name}
          team2Name={team2Name}
          gameId={game.id}
          gameState={gameState}
          refetchData={fetchData}
        />
      );
    }
  }

  // Not started
  return (
    <Box
      display="flex"
      flex={1}
      justifyContent="center"
      flexDirection="column"
      alignItems="center"
      gap={2}
      p={2}
    >
      <StandardButton
        onClick={handleStartGame}
        variant="outlined"
        color="primary"
      >
        Start Game
      </StandardButton>
    </Box>
  );
};

export { ControlCenter as ControlCenterPage };
export default ControlCenter;
